import _ from 'lodash';
import { Model } from 'backbone';
import { Rounds } from '../../collections';
import { User } from "../core";
const PLAYING = 'playing';
// const NOT_PLAYING = 'not-playing';
// const INJURED = 'injured';
const UNCERTAIN = 'uncertain';
// const INTERCHANGE = 'interchange';
// const EMERGENCY = 'emergency';
// const RESERVE = 'reserve';

const
	AVAILABLE = new Set([PLAYING, UNCERTAIN]),
	getScores = player => _.result(player, `stats.points_last_round`, 0),
	getProjOrRealScore = player => {
		const real_score = _.result(player, `stats.points_last_round`, 0),
			proj_score = _.result(player, `stats.proj_score`, 0)
		;
		return parseInt(real_score, 10) || proj_score;
	},
	getProjScore = player => {
		return _.result(player, `stats.proj_score`, 0);
	},
	without = (arr, index) => {
		const new_arr = arr.slice();
		new_arr.splice(index, 1);

		return new_arr;
	},
	isAvailable = ({ status }) => {
		return AVAILABLE.has(status);
	},
	isNotAvailable = player => !isAvailable(player),
	silent = { silent: true };

const substituteController = {
	get defaults() {
		return {
			player: {},
			substitute_player: {},
			players_by_id: {},
			player_position: undefined
		}
	},

	hasSubstituteClass({ substitute_player, player }) {
		const
			is_substitute = !_.isUndefined(substitute_player.id),
			player_exist = !_.isUndefined(player.id);

		return is_substitute && player_exist;
	},

	isSwappablePlayer({ substitute_player, player }) {
		const
			is_selected_for_substitute = player.id === substitute_player.id,
			is_not_locked = !player.is_locked;

		return is_selected_for_substitute && is_not_locked;
	},

	getSwapClass(args) {
		const {
				substitute_player,
				players_by_id,
				player,
				player_position
			} = args,

			is_swap_from_bench = substitute_player.pos === 'bench',
			is_on_bench = player_position === 'bench',
			subst_player_pos = parseInt(substitute_player.pos, 10),
			player_pos = parseInt(player_position, 10),
			{ positions } = players_by_id[substitute_player.id],
			is_accepted_position = positions.includes(player_pos);

		if (player.is_locked) {
			return 'is-not-accepted-for-swap';
		}

		let is_accepted_for_swap;

		if (is_swap_from_bench) {
			is_accepted_for_swap = is_on_bench || is_accepted_position;
		}
		else {
			if (is_on_bench) {
				is_accepted_for_swap = player.positions.includes(subst_player_pos);
			}
			else {
				is_accepted_for_swap = is_accepted_position &&
					player.positions.includes(subst_player_pos);
			}
		}

		return is_accepted_for_swap ? 'is-accepted-for-swap' : 'is-not-accepted-for-swap';
	},

	getClassName(args) {
		const options = _.extend({}, this.defaults, args);

		if (this.hasSubstituteClass(options)) {
			if (this.isSwappablePlayer(options)) {
				return 'is-swappable';
			}
			else {
				return this.getSwapClass(options);
			}
		}

		return '';
	}
};

const quickTradeController = {
	getClassName({ player, trade_out_player }) {
		const
			trade_out_payer_id = _.result(trade_out_player, 'id'),
			team_player_id = player.id;

		if (!trade_out_payer_id) {
			return '';
		}

		if (_.eq(trade_out_payer_id, team_player_id)) {
			return 'is-trade-out-state';
		}

		return 'is-not-accepted-for-swap';
	}
};

export class FantasyTeam extends Model {

	defaults() {
		return {
			max_team_size: 21,
			team_size: 0,
			name: '',
			lineup: {
				1: [0],
				2: [0, 0, 0],
				3: [0, 0],
				4: [0, 0],
				5: [0, 0],
				6: [0, 0, 0],
				bench: [0, 0, 0, 0, 0, 0, 0, 0],
				captain: undefined,
				vice_captain: undefined
			}
		}
	}

	initialize() {
		this.prefix = 'teams_classic/';

		return this.setShowMyUrl();
	}

	transformPlayersIdToObjects(
		{ players_by_id = {}, substitute_player = {}, trade_out_player = {} } = {}
	) {
		let lineup = this.get('lineup'),
			{ captain, vice_captain } = lineup,
			leaders = [captain, vice_captain],
			leaders_classes = ['is-captain', 'is-vice-captain'];
		const { not_played = [] } = this.attributes;
		lineup = _.chain(lineup)
			.omit(['captain', 'vice_captain'])
			.mapValues((row, position) => _.map(row, player_id => {
				const
					player = not_played.includes(player_id) ?
						{} :
						_.result(players_by_id, player_id, {}),
					leader_class_name = leaders_classes[leaders.indexOf(player_id)],
					substitute_class_name = substituteController.getClassName({
						player,
						substitute_player,
						players_by_id,
						player_position: position
					}),
					quick_trade_class_name = quickTradeController.getClassName({
						player,
						trade_out_player
					});

				return {
					...player,
					leader_class_name,
					substitute_class_name: `${substitute_class_name} ${quick_trade_class_name}`,
				};
			}))
			.value();

		this.set({
			lineup,
			captain,
			vice_captain
		}, silent);

		return this;
	}

	replacePlayers(from_id, replace_to) {
		let replacer = player => {
				const id = _.isNumber(player) ? player : this.getPlayerId(player);

				return id !== from_id ? player : replace_to;
			},
			lineup = _.chain(this.get('lineup'))
				.mapValues(
					value => _.isArray(value) ? value.map(replacer) : replacer(value)
				)
				.value();

		this.set({ lineup }, silent);

		return this;
	}

	swapPlayersOnField(players_id) {
		const swapPlayersOnFound = player_id => {
			let found = players_id.includes(player_id),
				inverted_index = players_id.indexOf(player_id) ^ 1;

			return found ? players_id[inverted_index] : player_id;
		};

		return _.chain(this.get('lineup'))
			.omit(['captain', 'vice_captain'])
			.mapValues(value => value.map(swapPlayersOnFound))
			.value();
	}

	isPlayerOnBench(player_id) {
		if (!player_id) {
			return false;
		}

		return this.get('lineup').bench.includes(player_id);
	}

	isPlayerOnField(player_id) {
		if (!player_id) {
			return false;
		}
		return this.team_players_id.includes(player_id);
	}
	getPlayerPositionInLineup(player_id) {

		const position = _.findKey(
			this.lineup_without_leads, item => _.isObject(_.find(item, { id: player_id }))
		);

		if (position === 'bench') {
			const index = this.get('lineup').bench.indexOf(player_id);

			return index < 4 ? 'int' : 'emg';
		}

		return parseInt(position, 10);
	}
	getPlayerPosition(player_id) {
		if (!this.isPlayerOnField(player_id)) {
			return false;
		}

		const position = _.findKey(this.lineup_without_leads, item => item.includes(player_id));

		if (position === 'bench') {
			const index = this.get('lineup').bench.indexOf(player_id);

			return index < 4 ? 'int' : 'emg';
		}

		return parseInt(position, 10);
	}

	swapLeaderFromBenchToField(type, players_id) {
		let player_id = this.get('lineup')[type],
			inverted_index = players_id.indexOf(player_id) ^ 1;

		const lineup = {
			...this.get('lineup'),
			[type]: players_id[inverted_index]
		};

		this.set({ lineup }, silent);

		return this;
	}

	swapPlayers(players) {
		let players_id = players.map(value => value.id),
			lineup = this.swapPlayersOnField(players_id);

		lineup = {
			...this.get('lineup'),
			...lineup,
		};

		this.set({ lineup }, silent);

		let { captain, vice_captain } = lineup;

		if (this.isPlayerOnBench(captain)) {
			this.swapLeaderFromBenchToField('captain', players_id);
		}

		if (this.isPlayerOnBench(vice_captain)) {
			this.swapLeaderFromBenchToField('vice_captain', players_id);
		}

		return this;
	}

	swapOnBench(id, players, direction) {
		const getPlayerWithDir = (index, dir, players) => {
			const
				player_for_swap = bench[index + dir],
				new_dir = dir + Math.sign(dir),
				player_not_locked = (
					!_.eq(_.result(players.get(player_for_swap), 'attributes.locked'), 1)
				);

			if(player_not_locked){
				return { player_for_swap, dir };
			}

			return getPlayerWithDir(index, new_dir, players);
		};

		const
			bench = _.result(this.get('lineup'), 'bench', []),
			index = bench.indexOf(parseInt(id, 10)),
			{ player_for_swap, dir } = getPlayerWithDir(index, direction, players),
			is_impossible_swap = (
				_.isUndefined(player_for_swap) ||
				players.get(id).get('locked')
			);

		if (is_impossible_swap) {
			return false;
		}

		bench[index + dir] = id;
		bench[index] = player_for_swap;

		this.set({ ...this.get('lineup'), bench }, silent);

		return true;
	}

	swapBenchUp(id, players) {
		return this.swapOnBench(id, players, -1);
	}

	swapBenchDown(id, players) {
		return this.swapOnBench(id, players, 1);
	}

	removePlayer({ id, replaceWith = 0 }) {
		return this.replacePlayers(id, replaceWith);
	}

	getTeamValue({ players_by_id = {} } = {}) {
		const sum = (value, player_id) => {
			const cost = _.result(players_by_id, `[${player_id}].cost`, 0);

			return value + cost;
		};

		return this.team_players_id.reduce(sum, 0);
	}

	get emergency_players_id() {
		return _.chain(this.get('lineup').bench)
			.slice(-4)
			.filter(_.identity)
			.value();
	}

	getProjScore({ players_by_id, is_active_round, is_matches_completed }) {
		const params = {
			lineup: this.lineup_without_leads,
			getPlayer: id => _.result(players_by_id, id),
			is_active_round,
			is_matches_completed
		};

		// console.log(this, this.lineup_without_leads);

		return  this.getTeamProjScoreWithPlayerStatus(params);
	}

	/**
	 * Get team projected score like a sum of real and projected score
	 * @param players_by_id - team players object
	 * @param lineup - user lineup
	 * @param getPlayer - function to get player info
	 * @param is_active_round - is round active
	 * @param is_matches_completed - is full lockout
	 */
	getTeamProjScoreWithPlayerStatus({ lineup, getPlayer, is_active_round, is_matches_completed }) {
		const scoreFunction = is_active_round ? getProjOrRealScore : getProjScore;
		const notAvailableOrDNP = player => {
			return is_active_round ? (
				isNotAvailable(player) || player.is_dnp
			) : isNotAvailable(player);
		};
		/**
		 * Convert lineup field without bench to array with player objects
		 * @type {Array.<Object>}
		 */
		const field = _(lineup)
			.omit('bench')
			.mapValues((lineup, field_position) => lineup.map(id => ({
				...getPlayer(id),
				field_position: parseInt(field_position, 10)
			}))).values().flatten().value();
		/**
		 * Convert bench field to array of player objects.
		 * Contain players only with score
		 * @type {Array.<Object>}
		 */
		const bench = _(lineup).result('bench')
			.filter(_.identity)
			.map((id, index) => ({
				...getPlayer(id),
				is_reserve: index < 4,
				is_emergency: index >= 4
			}))
			.filter(isAvailable)
			.filter(_.flow( scoreFunction, _.isNumber));
		const
			/**
			 * Calculate scores of everyone from the field, included double score of captain
			 * or vice captain
			 * @type {Int}
			 */
			field_points = _(field).filter(player => !notAvailableOrDNP(player))
				.map(scoreFunction).filter(_.isNumber).sum(),
			/**
			 * Get positions of all DNP players
			 * @type {Array.<Int>}
			 */
			field_dnp_position = field.filter(notAvailableOrDNP)
				.map(player => player.field_position);

		if (!is_matches_completed) {

			const bench_scores = _(bench)
				.filter(({ is_reserve }) => is_reserve)
				.filter(player => !notAvailableOrDNP(player))
				.map(scoreFunction).
				sum();
			return field_points + bench_scores;
		}

		/**
		 * Calculate all points of players from the bench,
		 * who can replace DNP players on the field
		 * @type {Array.<Object>}
		 */
		const scored_on_bench = this.getBestDNPReplaceCombination(
			field_dnp_position,
			bench,
		);

		let scored_ids = scored_on_bench.map(this.getPlayerId),
			/**
			 * Get players from bench who do not replace anyone on the field
			 * @type {Array.<Object>}
			 */
			unused = bench.filter(player => !scored_ids.includes(player.id));

		/**
		 * Get auto emergency
		 * @type {Array.<Object>}
		 */
		const auto_emergency = unused.slice(0, 4);
		scored_on_bench.push(...auto_emergency);

		const bench_scores = _.sum(scored_on_bench.map(scoreFunction));

		return field_points + bench_scores;
	}


	/**
	 * Calculate team score during rolling lockout.
	 * @desc Field players score + Reserve players score.
	 * @param lineup {object}
	 * @param getPlayer {function} - Function to get players by ID
	 * @return {{team_score: Int, not_counted_players: Array.<Int>}}
	 */
	getRollingLockoutTeamScore({ lineup, getPlayer }) {
		/**
		 * Convert lineup without bench to array of player objects
		 * @type {Array.<Object>}
		 */
		const field = _(lineup)
			.omit('bench')
			.mapValues((lineup, field_position) => lineup.map(id => ({
				...getPlayer(id),
				field_position: parseInt(field_position, 10)
			}))).values().flatten().value();
		/**
		 * Convert bench field to array of player objects.
		 * Contain players only with score
		 * @type {Array.<Object>}
		 */
		const bench = _(lineup).result('bench')
			.filter(_.identity)
			.map((id, index) => ({
				...getPlayer(id),
				is_reserve: index < 4,
				is_emergency: index >= 4
			}))
			.filter(_.flow(getScores, _.isNumber));

		const
			/**
			 * Calculate scores of everyone on the field,
			 * included double score of captain or vice captain
			 * @type {Int}
			 */
			field_points = _(field).map(getScores).filter(_.isNumber).sum(),
			/**
			 * Calculate reserve scores.
			 * @type {Int}
			 */
			reserve_scores = _.sum(
				bench.filter(_.matches({ is_reserve: true })).map(getScores)
			),
			/**
			 * Total team score
			 * @type {Int}
			 */
			team_score = field_points + reserve_scores,
			// team_score = field_points,
			/**
			 * Emergency players who scored but not counted in total team scores
			 * @type {Array.<Int>}
			 */
			not_counted_players = bench
				.filter(_.matches({ is_emergency: true }))
				.map(this.getPlayerId);

		return { team_score, not_counted_players };
	}

	getFullLockoutTeamScore({ lineup, getPlayer }) {
		/**
		 * Convert lineup field without bench to array with player objects
		 * @type {Array.<Object>}
		 */
		const field = _(lineup)
			.omit('bench')
			.mapValues((lineup, field_position) => lineup.map(id => ({
				...getPlayer(id),
				field_position: parseInt(field_position, 10)
			}))).values().flatten().value();

		/**
		 * Convert bench field to array of player objects.
		 * Contain players only with score
		 * @type {Array.<Object>}
		 */
		const bench = _(lineup).result('bench')
			.filter(_.identity)
			.map((id, index) => ({
				...getPlayer(id),
				is_reserve: index < 4,
				is_emergency: index >= 4
			}))
			.filter(_.flow(getScores, _.isNumber));

		const
			/**
			 * Calculate scores of everyone from the field, included double score of captain
			 * or vice captain
			 * @type {Int}
			 */
			field_points = _(field).map(getScores).filter(_.isNumber).sum(),
			/**
			 * Get positions of all DNP players
			 * @type {Array.<Int>}
			 */
			field_dnp_position = field.filter(_.matches({ is_dnp: true }))
				.map(player => player.field_position);

		const bench_data = { scores: 0, unused: bench };

		/**
		 * Calculate all points of players from the bench,
		 * who can replace DNP players on the field
		 * @type {Array.<Object>}
		 */
		const scored_on_bench = this.getBestDNPReplaceCombination(
			field_dnp_position,
			bench,
		);

		let scored_ids = scored_on_bench.map(this.getPlayerId),
			/**
			 * Get players from bench who do not replace anyone on the field
			 * @type {Array.<Object>}
			 */
			unused = bench.filter(player => !scored_ids.includes(player.id));

		/**
		 * Get auto emergency
		 * @type {Array.<Object>}
		 */
		const auto_emergency = unused.slice(0, 4);
		scored_on_bench.push(...auto_emergency);
		scored_ids = scored_on_bench.map(this.getPlayerId);

		/**
		 * Get players from bench who do not replace anyone
		 * on the field and counted in team score
		 * @type {Array.<Object>}
		 */
		unused = bench.filter(player => !scored_ids.includes(player.id));
		bench_data.scores = _.sum(scored_on_bench.map(getScores));
		bench_data.unused = unused.map(this.getPlayerId);

		const
			/**
			 * Total team score
			 * @type {Int}
			 */
			team_score = field_points + bench_data.scores,
			// team_score = field_points,
			/**
			 * Emergency players who scored but not counted in total team scores
			 * @type {Array.<Int>}
			 */
			not_counted_players = bench_data.unused;

		return { team_score, not_counted_players };
	}

	getBestDNPReplaceCombination(positions = [], players = []) {
		if (!_.size(positions) || !_.size(players)) {
			return [];
		}

		players = players
			/**
			 * Get only those players who can replace someone on the field.
			 * @type {Array.<Object>}
			 */
			.filter(player => player.positions.some(
				position => positions.includes(position)
			))
			/**
			 * Set rank to every player
			 * @type {Array.<Object>}
			 */
			.map((player, index) => ({ ...player, rank: index }));

		const possible_positions = _.flatten(players.map(player => player.positions));
		positions = positions.filter(pos => possible_positions.includes(pos));

		/**
		 * Pull for all possible combinations
		 * @type {Array}
		 */
		const combinations = [];

		/**
		 * Generator of possible combinations
		 * @param positions {Array.<Int>} Filed DNP positions
		 * @param players {Array.<Object>} Array with players from bench
		 * @param size {number} Size of players array
		 * @param stack {Array.<Object>} Stack of finded combination
		 * @return {*}
		 */
		const fillCombination = (positions, players, size = 0, stack = []) => {
			if (!_.size(positions) || !_.size(players) || _.size(stack) === size) {
				return combinations.push(stack);
			}

			_.each(positions, (pos, index) => {
				_.each(players, (player, player_index) => {
					if (player.positions.includes(pos)) {
						const
							pos_arr = without(positions, index),
							players_arr = without(players, player_index);

						fillCombination(pos_arr, players_arr, size, [...stack, player]);
					}
				});
			});

			return void(0);
		};

		fillCombination(positions, players, _.size(players));

		/**
		 * Get combination with the minimal rank
		 */
		return _.minBy(
			combinations,
			stack => _.sum(stack.map(item => item.rank))
		) || [];
	}

	/**
	 * Calculate total team score based on stats.points_last_round field.
	 * @param players_by_id {Object.<Object>}  Object with players where player id is a key.
	 * @param is_full_lockout boolean  Boolean param to check lockout status
	 * @return {{team_score: Int, not_counted_players: Array }} Return
	 * calculated team_score value and array of players who scored
	 * but do not counted in total team scores.
	 */
	getTeamScore({ players_by_id, is_full_lockout }) {
		const params = {
			lineup: this.lineup_without_leads,
			getPlayer: id => _.result(players_by_id, id)
		};

		// this.getTeamProjScore({ players_by_id });

		if (is_full_lockout) {
			return this.getFullLockoutTeamScore(params);
		}

		return this.getRollingLockoutTeamScore(params);
	}

	getPlayerId(value) {
		return _.isNumber(value) ? value : _.result(value, 'id', 0);
	}

	get lineup() {
		let { captain, vice_captain, lineup } = this.attributes;

		lineup = _.chain(lineup)
			.mapValues(
				value => _.isArray(value) ? _.map(value, this.getPlayerId) : value
			)
			.value();

		return {
			lineup: {
				captain,
				vice_captain,
				...lineup
			}
		};
	}

	get team_players_id() {
		return _.chain(this.get('lineup'))
			.omit(['captain', 'vice_captain'])
			.values()
			.flatten()
			.filter(_.identity)
			.value();
	}

	get team_size() {
		return _.size(this.team_players_id);
	}

	get lineup_without_leads() {
		return _.omit(this.get('lineup'), ['captain', 'vice_captain']);
	}

	getWithBench(positions) {
		let pos = positions.slice();

		if (!pos.includes('bench')) {
			pos.push('bench');
		}

		return pos;
	}

	getSizeDiff(lineup, pos_id) {
		const
			lineup_by_pos = lineup[pos_id] || [],
			initial = _.size(lineup_by_pos),
			actual = _.size(lineup_by_pos.filter(_.identity));

		return { initial, actual };
	}

	getFilledInfo() {
		const
			bench = this.getSizeDiff(this.get('lineup'), 'bench'),
			initial_field_size = this.get('max_team_size') - bench.initial,
			field = {
				actual: this.team_size - bench.actual,
				initial: initial_field_size
			};

		return {
			field: { ...field },
			bench: { ...bench }
		};
	}

	setPlayer({ player_id, position }) {
		const
			row = this.lineup_without_leads[position],
			index_of_empty_pos = _.findIndex(row, value => !value);

		row.splice(index_of_empty_pos, 1, player_id);

		this.set({
			lineup: {
				...this.get('lineup'),
				[position]: row
			}
		}, silent);

		return this;
	}

	get empty_positions() {
		return _(this.get('lineup'))
			.map((value, key) => value.filter(_.negate(_.identity)).fill(key))
			.values()
			.flatten()
			.map(key => parseInt(key, 10) || key)
			.value()
	}

	isPositionsFilled(positions, exclude_bench) {
		const comparator = _.partial(
			this.getSizeDiff,
			this.lineup_without_leads
		);

		let pos = positions.slice();

		if (!exclude_bench) {
			pos = this.getWithBench(pos);
		}

		return pos.every(pos_id => {
			const { initial, actual } = comparator(pos_id);

			return initial === actual;
		});
	}

	getAvailablePositions(positions) {
		const comparator = _.partial(
			this.getSizeDiff,
			this.lineup_without_leads
		);

		return this.getWithBench(positions).filter(pos_id => {
			const { initial, actual } = comparator(pos_id);

			return initial !== actual;
		});
	}

	get is_filled() {
		return this.get('max_team_size') === this.team_size;
	}

	setShowMyUrl() {
		this.url = 'show_my';

		return this;
	}

	setShowUrl() {
		this.url = 'show';

		return this;
	}

	setUpdateUrl() {
		this.url = 'update';

		return this;
	}

	setAutoFillUrl() {
		this.url = 'auto_fill';

		return this;
	}

	getAvatarUrl(user_id) {
		const path_url = process.env.AVATARS_PATH + user_id,
			version = User.get('avatar_version') || Date.now();

		return `${path_url}.png?v=${version}`;
	}

	isUnlimitedTrades({ start_round, start_round_id }) {
		if (_.isUndefined(start_round) || _.isUndefined(start_round_id) || start_round_id <= 0) {
			return true;
		}

		const
			is_valid = Boolean(this.get('valid')),
			is_complete = Boolean(this.get('complete')),
			is_round_full_lockout = (
				start_round.is_full_lockout || start_round.is_complete
			);

		return ![is_complete, is_valid, is_round_full_lockout].every(_.identity);
	}

	isBeganToScore({ start_round, start_round_id }) {
		if (_.isUndefined(start_round) || _.isUndefined(start_round_id) || start_round_id <= 0) {
			return false;
		}

		const
			is_valid = Boolean(this.get('valid')),
			is_complete = Boolean(this.get('complete')),
			start_round_began = !Rounds.isScheduled(start_round.get('status'));

		return [is_complete, is_valid, start_round_began].every(_.identity);
	}

	eachPlayer(cb) {
		_.each(this.get('lineup'), (line, position) => {
			_.each(line, (player, index) => {
				player && cb(player, position, index, line);
			});
		});

		return this;
	}

	map(cb) {
		const stack = [];

		this.eachPlayer((player, position, index, line) => {

			stack.push(cb(player, position, index, line));
		});

		return stack;
	}

	isSomebodyPlaying(players_by_id = {}) {
		return this.map(
			res => _.result((players_by_id[res] || res), 'is_playing_now', false)
		).some(_.identity);
	}

	isNobodyPlayed(players_by_id = {}) {
		return this.map(
			res => _.result(players_by_id[res] || res, 'stats.points_last_round', false)
		).every(_.negate(_.identity));
	}

	get is_somebody_playing() {
		return this.isSomebodyPlaying();
	}

	get is_nobody_played() {
		return this.isNobodyPlayed();
	}

	getPlayerById(player_id) {
		let playerObject = {};

		this.eachPlayer(player => {
			if (player.id === player_id) {
				playerObject = player;
			}
		});

		return playerObject;
	}

	makeSwap([from, to]) {
		const
			lineup = this.get('lineup'),
			tmp = lineup[from.position][from.index];

		lineup[from.position][from.index] = lineup[to.position][to.index];
		lineup[to.position][to.index] = tmp;

		this.set({ lineup }, silent);

		return this;
	}
}

export default FantasyTeam;